/**
 * The analyst profile contract (AIC-815), shared by tracker-api (routes/profile.ts validates and stores it; the outreach
 * drafts read it) and the analyst portal (the My profile page). Pure and dependency-free: the portal bundles it.
 *
 * Codes are stable: a stored region or language keeps its meaning when a label is reworded. Adding one is additive;
 * renaming one is not. The migration's CHECK (db/migrations/0064_analyst_profile.sql) lists the same region codes.
 */

/** How well an analyst speaks a language, best first. */
export const LANGUAGE_LEVELS = ["native", "fluent", "call", "reading"] as const;
export type LanguageLevel = (typeof LANGUAGE_LEVELS)[number];
export const LANGUAGE_LEVEL_LABEL: Readonly<Record<LanguageLevel, string>> = { native: "Native", fluent: "Fluent", call: "Can hold a call", reading: "Reading only" };

/** The languages the page offers, by stable code. */
export const LANGUAGES = [
  ["en", "English"], ["es", "Spanish"], ["pt", "Portuguese"], ["fr", "French"], ["de", "German"], ["it", "Italian"], ["ja", "Japanese"],
  ["zh", "Mandarin Chinese"], ["ko", "Korean"], ["hi", "Hindi"], ["ar", "Arabic"], ["nl", "Dutch"], ["pl", "Polish"], ["he", "Hebrew"],
] as const;
export type LanguageCode = (typeof LANGUAGES)[number][0];
export const LANGUAGE_CODES = LANGUAGES.map(([c]) => c) as readonly LanguageCode[];
export const languageLabel = (code: string): string => LANGUAGES.find(([c]) => c === code)?.[1] ?? code;

export interface LanguageEntry { language: LanguageCode; level: LanguageLevel }
export const MAX_LANGUAGES = LANGUAGES.length;

/**
 * Region coverage as the Data Layer's geography: three regions of three subregions each, matching a vendor's headquarters.
 * The Data Layer's own geography map (the GEOGRAPHICALMAP dimension the estimates read) carries names, not codes, so these
 * codes are ours: `<region>.<subregion>`.
 */
export const REGIONS = [
  { code: "americas", label: "Americas", subregions: [["americas.us", "United States"], ["americas.ca", "Canada"], ["americas.latam", "Latin America"]] },
  { code: "emea", label: "EMEA", subregions: [["emea.weu", "Western Europe"], ["emea.cee", "Central and Eastern Europe"], ["emea.mea", "Middle East and Africa"]] },
  { code: "apac", label: "Asia/Pacific", subregions: [["apac.jp", "Japan"], ["apac.cn", "China"], ["apac.rest", "Asia/Pacific (excluding Japan and China)"]] },
] as const;
export type RegionCode = (typeof REGIONS)[number]["subregions"][number][0];
export const REGION_CODES = REGIONS.flatMap((r) => r.subregions.map(([c]) => c)) as readonly RegionCode[];
export const SUBREGION_COUNT = REGION_CODES.length;

/** "Americas (all)" for a fully covered region, else the subregions' names; "" when none. */
export function regionSummary(codes: readonly string[]): string {
  const parts = REGIONS.flatMap((r) => {
    const on = r.subregions.filter(([c]) => codes.includes(c));
    return on.length === 0 ? [] : on.length === r.subregions.length ? [`${r.label} (all)`] : [on.map(([, l]) => l).join(", ")];
  });
  return parts.join("; ");
}

/** A line such as "English (native), Spanish (fluent)": the level in lower case, as the page words it. */
export const languageSummary = (entries: readonly LanguageEntry[]): string =>
  entries.map((e) => `${languageLabel(e.language)} (${LANGUAGE_LEVEL_LABEL[e.level].toLowerCase()})`).join(", ");

/** Which signature the drafts carry: the IDC profile's (name, title when loaded, email), or the analyst's own text. */
export const SIGNATURE_MODES = ["idc_profile", "own"] as const;
export type SignatureMode = (typeof SIGNATURE_MODES)[number];
export const SIGNATURE_MAX = 1000;

/**
 * Which of the program's reminders are drafted for the analyst: the first and the second day step of the program's reminder
 * cadence (the reminder_cadence setting, 3 and 7 by default; the columns keep their old names, `day3` and `day7`) and the
 * reminder before a booked call (any `slot<H>h`). A day step an admin adds beyond the first two is always drafted. Every switch
 * is on until the analyst turns it off, so a profile never written changes nothing.
 */
export interface ReminderPrefs { day3: boolean; day7: boolean; slot: boolean }
export const DEFAULT_REMINDER_PREFS: ReminderPrefs = { day3: true, day7: true, slot: true };
/** The switch that governs a reminder kind (`day5`, `slot24h`…) under a cadence's day steps, or null when none does. */
export function reminderPrefFor(kind: string, days: readonly number[]): keyof ReminderPrefs | null {
  const d = /^day(\d+)$/.exec(kind);
  if (d) {
    const at = [...new Set(days)].sort((a, b) => a - b).indexOf(Number(d[1]));
    return at === 0 ? "day3" : at === 1 ? "day7" : null;
  }
  return /^slot\d+h$/.test(kind) ? "slot" : null;
}
/** True unless the analyst turned this reminder kind off. */
export const reminderWanted = (prefs: ReminderPrefs, kind: string, days: readonly number[]): boolean => {
  const k = reminderPrefFor(kind, days);
  return k === null || prefs[k];
};

/** What an analyst can report about their record, once: the code, how they say it, and how an admin reads it. */
export const REPORT_REASONS = [
  { code: "vendors_missing", label: "Some of my vendors are missing", about: "some of their vendors are missing" },
  { code: "someone_elses_vendors", label: "I'm seeing someone else's vendors", about: "they are seeing someone else's vendors" },
  { code: "not_me", label: "This owner name isn't me", about: "this owner name isn't them" },
  { code: "profile_wrong", label: "My IDC profile is missing or wrong", about: "their IDC profile is missing or wrong" },
] as const;
export type ReportReason = (typeof REPORT_REASONS)[number]["code"];
export const REPORT_REASON_CODES = REPORT_REASONS.map((r) => r.code) as readonly ReportReason[];
export const reportLabel = (code: string): string => REPORT_REASONS.find((r) => r.code === code)?.label ?? "Your report";
/**
 * AIC-987: "Request mapping" from someone whose account maps to no analyst joins People's reports with this reason. It is not one of
 * REPORT_REASONS: Report a mismatch never offers it, and only the server sets it.
 */
export const MAPPING_REQUEST_REASON = "mapping_request";
export const reportAbout = (code: string): string => (code === MAPPING_REQUEST_REASON
  ? "their account isn't mapped to an analyst, so they see no work"
  : REPORT_REASONS.find((r) => r.code === code)?.about ?? "something on their record is wrong");

/** Extra CC addresses on the analyst's drafts: IDC addresses only. */
export const MAX_EXTRA_CC = 10;
export const IDC_ADDRESS = /^[^@\s,;<>"]+@idc\.com$/i;
/** The addresses in "a@idc.com, b@idc.com" (comma, semicolon or whitespace separated), lower-cased and de-duplicated; null when any is not an IDC address. */
export function parseExtraCc(text: string): string[] | null {
  const parts = text.split(/[,;\s]+/).map((p) => p.trim()).filter(Boolean);
  if (parts.some((p) => !IDC_ADDRESS.test(p))) return null;
  return [...new Set(parts.map((p) => p.toLowerCase()))];
}

/** The loaded idc.com profile (nothing loads it yet; db/migrations/0064). Every field is optional: a load fills what the page has. */
export interface IdcProfile { title?: string; bio?: string; research_areas?: string[]; photo_url?: string; profile_url?: string }

/**
 * The signature lines a draft ends with. IDC profile: the name, the title when a profile is loaded, then the email. Own:
 * the analyst's text, a line per line (blank lines dropped); an own signature with no text falls back to the profile's.
 */
export function signatureLines(p: { mode: SignatureMode; text: string | null; name: string; email: string; title?: string | null }): string[] {
  if (p.mode === "own" && p.text) {
    const own = p.text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
    if (own.length) return own;
  }
  return [p.name, ...(p.title ? [p.title] : []), p.email].filter(Boolean);
}
